import { Injectable, NgZone } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, fromEvent, merge, interval, of } from 'rxjs';
import { switchMap, take, skipWhile, takeUntil, filter } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LocalStorageKey } from '@enums-global/enum-local-storage-key';
import { AuthenticationService } from '@services/authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private readonly _sessionTimeout = Math.floor(
    (environment.sessionTimeoutMinutes || 0) * 60 * 1000
  );
  private readonly _userActivityEvents: Array<any>[] = [
    [document, 'click'],
    [document, 'wheel'],
    [document, 'scroll'],
    [document, 'mousemove'],
    [document, 'keyup'],
    [window, 'resize'],
    [window, 'scroll'],
    [window, 'mousemove']
  ];

  constructor(
    private _ngZone: NgZone,
    private _auth0Service: AuthService,
    private _authenticationService: AuthenticationService
  ) {}

  init(): void {
    if (!this._sessionTimeout) {
      return;
    }

    const userActivityEvents$: Observable<any>[] = this._userActivityEvents.map(
      (e) => fromEvent(e[0], e[1])
    );

    this._ngZone.runOutsideAngular(() => {
      merge(of(true), ...userActivityEvents$)
        .pipe(
          switchMap(() => interval(1000)),
          skipWhile((interval) => {
            const now = Date.now();
            const lastActive = parseInt(
              localStorage.getItem(LocalStorageKey.LAST_ACTIVE) || '0',
              10
            );
            const sessionValid =
              !lastActive || now < lastActive + this._sessionTimeout;

            if (interval === 0 && sessionValid) {
              localStorage.setItem(LocalStorageKey.LAST_ACTIVE, now.toString());
            }

            return sessionValid;
          }),
          take(1),
          takeUntil(
            this._auth0Service.isAuthenticated$.pipe(
              filter((isAuthenticated) => !isAuthenticated)
            )
          )
        )
        .subscribe(() => this._authenticationService.logout());
    });
  }
}
